import { useMemo } from "react";
import { Alert, Box, Stack, useTheme } from "@mui/material";
import { Button, FoodsTags, MRTGrid, Typography, WhiteBox } from "@foodpilot/foods";
import type { MRT_ColumnDef, MRT_Row } from "material-react-table";
import { useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { useActionPlanTemplate } from "src/api/client-api/actionPlanTemplates/useActionPlanTemplate";
import { useTemplateActionCatalog } from "src/api/client-api/actionPlanTemplates/useTemplateActionCatalog";
import { useGetOneSsq } from "src/api/sure-api/ssq/useGetOneSsq";
import { ACTION_PLAN_TEMPLATE, CREATE, useFoodpilotContext } from "src/context/FoodpilotContext";
import { URLHandler } from "src/services/URLHandler";
import { formatVersion } from "src/utils/formatting";
import { SettingsPageHeader } from "../components/SettingsPageHeader";
import { TemplateStatusBadge } from "./TemplateStatusBadge";
import { TemplateConsultationActions } from "./TemplateConsultationActions";

type ActionRow = {
    position: number;
    title: string;
    category: string;
    categories: { id: number; title: string }[];
};

export const ActionPlanTemplateDetail = () => {
    const { templateId } = useParams();
    const query = useActionPlanTemplate(templateId);
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const { canUser } = useFoodpilotContext();
    const template = query.data;
    const ssq = useGetOneSsq(template?.ssqId);
    const catalog = useTemplateActionCatalog(
        template?.actions.map((action) => action.actionId) ?? [],
        template?.ssqVersionId,
    );
    const listUrl = URLHandler.settings.configurationPath("platform-setup", "action-plan-templates");
    const selectedVersion = ssq.ssq?.versions?.find((version) => version.id === template?.ssqVersionId);
    const versionLabel =
        selectedVersion ? formatVersion(selectedVersion)
        : ssq.isLoading ? t("settings.action_plan_templates.form.loading_options", "Loading options…")
        : ssq.isError ? t("settings.action_plan_templates.ssq_unavailable", "SSQ unavailable")
        : template ?
            t(
                "settings.action_plan_templates.publish.ssq_version_unavailable",
                "The selected SSQ version is no longer available. Create a new template with another version.",
            )
        :   "";
    const rows = useMemo<ActionRow[]>(
        () =>
            (template?.actions ?? []).map((item) => {
                const action = catalog.data?.get(item.actionId);
                const categories = [
                    ...new Map(
                        (action?.csrThemes ?? []).map((theme) => {
                            const category = theme.parent ?? theme;

                            return [category.id, category] as const;
                        }),
                    ).values(),
                ].sort((a, b) => a.title.localeCompare(b.title, i18n.resolvedLanguage));

                return {
                    position: item.position,
                    title:
                        action?.title ??
                        (catalog.isLoading ?
                            t("settings.action_plan_templates.loading", "Loading templates…")
                        :   t("settings.action_plan_templates.detail.action_unavailable", "Action unavailable")),
                    category: categories.map((category) => category.title).join(", "),
                    categories,
                };
            }),
        [template?.actions, catalog.data, catalog.isLoading, t, i18n.resolvedLanguage],
    );
    const columns = useMemo<MRT_ColumnDef<ActionRow>[]>(
        () => [
            {
                accessorKey: "title",
                muiTableHeadCellProps: { sx: { width: "70%" } },
                muiTableBodyCellProps: { sx: { width: "70%" } },
                header: t("settings.action_plan_templates.detail.action_name", "Action name"),
            },
            {
                accessorKey: "category",
                muiTableHeadCellProps: { sx: { width: "25%" } },
                muiTableBodyCellProps: { sx: { width: "25%" } },
                header: t("settings.action_plan_templates.detail.category", "Category"),
                Cell: (props: { row: MRT_Row<ActionRow> }) => (
                    <Stack direction="row" flexWrap="wrap" gap={theme.spacing(1)}>
                        {props.row.original.categories.length ?
                            props.row.original.categories.map((category) => (
                                <FoodsTags
                                    key={`${category.id}-template-action-category`}
                                    label={category.title}
                                    colors={{ background: theme.custom.grey[400], foreground: theme.custom.grey[2500] }}
                                />
                            ))
                        :   <Typography>—</Typography>}
                    </Stack>
                ),
            },
        ],
        [t, i18n.resolvedLanguage, theme],
    );

    return (
        <Box sx={{ padding: { xs: theme.spacing(2), md: theme.spacing(4) } }}>
            <Stack
                direction={{ xs: "column", lg: "row" }}
                justifyContent="space-between"
                alignItems="flex-start"
                gap={theme.spacing(2)}
            >
                <SettingsPageHeader
                    title={template?.name ?? t("settings.action_plan_templates.title", "Action plan templates")}
                    subtitle={t(
                        "settings.action_plan_templates.detail.description",
                        "Set up your action plan template and add actions to it.",
                    )}
                    breadcrumbs={[
                        { label: t("settings.platform.title", "Platform"), to: URLHandler.settings.configuration() },
                        {
                            label: t("settings.platform.platform_setup.title", "Platform setup"),
                            to: URLHandler.settings.configurationPath("platform-setup"),
                        },
                        { label: t("settings.action_plan_templates.title", "Action plan templates"), to: listUrl },
                        ...(template ? [{ label: template.name }] : []),
                    ]}
                />
                {template && (
                    <TemplateConsultationActions
                        template={template}
                        canCreate={canUser(CREATE, ACTION_PLAN_TEMPLATE)}
                        onEdit={() => navigate(`${listUrl}/${template.id}/edit`)}
                        onDeleted={() => navigate(listUrl, { replace: true })}
                    />
                )}
            </Stack>
            {query.isError ?
                <Stack role="alert" gap={theme.spacing(2)}>
                    <Typography>
                        {t(
                            "settings.action_plan_templates.detail.load_failed",
                            "Unable to load this template. It may have been deleted, or you may not have access to it.",
                        )}
                    </Typography>
                    <Button variant="secondary" onClick={() => query.refetch()}>
                        {t("settings.action_plan_templates.retry", "Try again")}
                    </Button>
                </Stack>
            : !template ?
                <Typography role="status">
                    {t("settings.action_plan_templates.loading", "Loading templates…")}
                </Typography>
            :   <Stack gap={theme.spacing(3)}>
                    {template.status === "archived" && (
                        <Alert severity="info">
                            <Typography>
                                {t(
                                    "settings.action_plan_templates.detail.archived",
                                    "This template is archived and can't be edited. Unarchive it to make changes.",
                                )}
                            </Typography>
                        </Alert>
                    )}
                    <Typography variant="h3">
                        {t("settings.action_plan_templates.detail.general_information", "General information")}
                    </Typography>
                    <WhiteBox sx={{ p: theme.spacing(3) }}>
                        <Stack gap={theme.spacing(3)}>
                            <Stack direction="row" flexWrap="wrap" gap={theme.spacing(1)}>
                                <Typography variant="body-bold">
                                    {t("settings.action_plan_templates.form.name", "Template name")}:
                                </Typography>
                                <Typography>{template.name}</Typography>
                            </Stack>
                            <Stack direction={{ xs: "column", sm: "row" }} flexWrap="wrap" gap={theme.spacing(4)}>
                                <Stack direction="row" alignItems="center" gap={theme.spacing(1)}>
                                    <Typography variant="body-bold">
                                        {t("settings.action_plan_templates.column.status", "Status")}:
                                    </Typography>
                                    <TemplateStatusBadge status={template.status} />
                                </Stack>
                                <Stack direction="row" flexWrap="wrap" alignItems="center" gap={theme.spacing(1)}>
                                    <Typography variant="body-bold">
                                        {t("settings.action_plan_templates.column.ssq", "SSQ")}:
                                    </Typography>
                                    <Typography>
                                        {ssq.ssq?.title ??
                                            template.ssqTitle ??
                                            t("settings.action_plan_templates.ssq_unavailable", "SSQ unavailable")}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" flexWrap="wrap" alignItems="center" gap={theme.spacing(1)}>
                                    <Typography variant="body-bold">
                                        {t("settings.action_plan_templates.form.ssq_version", "SSQ version")}:
                                    </Typography>
                                    <Typography>{versionLabel}</Typography>
                                </Stack>
                            </Stack>
                            <Stack direction="row" flexWrap="wrap" gap={theme.spacing(1)}>
                                <Typography variant="body-bold">
                                    {t("settings.action_plan_templates.column.sector", "Industry sector")}:
                                </Typography>
                                <Typography>{template.sectorName}</Typography>
                            </Stack>
                        </Stack>
                    </WhiteBox>
                    <Stack direction="row" alignItems="center" gap={theme.spacing(2)} sx={{ mt: theme.spacing(2) }}>
                        <Typography variant="h3">
                            {t("settings.action_plan_templates.detail.added_actions", "Added actions")}
                        </Typography>
                        <FoodsTags label={String(template.actionCount)} />
                    </Stack>
                    {catalog.isError && (
                        <Alert
                            severity="error"
                            action={
                                <Button variant="text" onClick={() => catalog.refetch()}>
                                    {t("settings.action_plan_templates.retry", "Try again")}
                                </Button>
                            }
                        >
                            <Typography>
                                {t(
                                    "settings.action_plan_templates.detail.catalog_failed",
                                    "Unable to load action details. Please try again.",
                                )}
                            </Typography>
                        </Alert>
                    )}
                    <MRTGrid
                        columns={columns}
                        data={rows}
                        getRowId={(action) => `${action.position}-template-action-row`}
                        layoutMode="semantic"
                        enableRowSelection={false}
                        enableColumnActions={false}
                        enableColumnFilters={false}
                        enableRowNumbers
                        enableSorting={false}
                        enableTopToolbar={false}
                        enableBottomToolbar={false}
                        enablePagination={false}
                        muiPaginationStackProps={{ sx: { display: "none" } }}
                        state={{ isLoading: template.actionCount > 0 && catalog.isLoading }}
                        localization={{
                            noRecordsToDisplay: t(
                                "settings.action_plan_templates.detail.empty_actions",
                                "No actions added yet.",
                            ),
                        }}
                    />
                </Stack>
            }
        </Box>
    );
};
